import type { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import type { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import type { IMappedGeoMappingItem } from 'partner360/components/filters'
import { filterBusinessPriorities } from 'partner360/features/keyInitiatives'
import type { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type {
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitPartnerData,
  ICockpitPlanningPeriod,
  IEnrichedCockpitFinSobRow,
} from '../../model/types'
import { getFilteredFinSobRows } from '../getFilteredFinSobRows'
import { getRevenueAndSobBaseline, getRevenueAndSobPlan, OVERALL_CATEGORY } from '../revenueAndSob'
import { isActualsFinSobRow } from '../revenueAndSob/isActualsFinSobRow'

import { getCockpitInitiativesInScope } from './getCockpitInitiativesInScope'
import { getCockpitInitiativesQuality } from './getCockpitInitiativesQuality'

export interface IGetCockpitInitiativesPlanQualityParams {
  initiatives: ICockpitKeyInitiative[]
  rows: IEnrichedCockpitFinSobRow[]
  partners: ICockpitPartnerData[]
  geoMappingItems: IMappedGeoMappingItem[]
  businessPriorities: IKIBusinessPriority[]
  keyObjectives?: readonly IKeyObjective[]
  offering: string
  fYear: number
  currentFQuarter: number
  fQuarterActualsPercentage: number
  selectedMetric: ActualsValueTypes
  selectedCurrency: string
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
  isLocalCurrency: boolean
  selectedCategory?: string | null
}

const getBusinessPrioritiesForOffering = ({
  priorities,
  partners,
  geoMappingItems,
  initiatives,
  fYear,
  offering,
}: {
  priorities: IKIBusinessPriority[]
  partners: ICockpitPartnerData[]
  geoMappingItems: IMappedGeoMappingItem[]
  initiatives: ICockpitKeyInitiative[]
  fYear: number
  offering: ProgramTypes
}) => {
  const initiativePartnerIds = new Set(initiatives.map(({ PartnerId }) => PartnerId))
  const normalize = (value: string) => value.trim().toLowerCase()
  const marketShortNameByName = new Map(
    geoMappingItems.flatMap(({ itemId, name, shortName }) => [
      [normalize(itemId), shortName],
      [normalize(name), shortName],
    ])
  )
  const markets = new Set(
    partners
      .filter(({ PartnerId }) => initiativePartnerIds.has(PartnerId))
      .map(({ GeoMarket }) => marketShortNameByName.get(normalize(GeoMarket)))
      .filter(Boolean)
  )
  const prioritiesByUid = new Map<string, IKIBusinessPriority>()
  const marketsInScope = markets.size ? markets : new Set<string | null>([null])

  marketsInScope.forEach((market) => {
    filterBusinessPriorities({ priorities, fYear, programType: offering, market }).forEach(
      (priority) => prioritiesByUid.set(priority.uid.trim().toLowerCase(), priority)
    )
  })

  return Array.from(prioritiesByUid.values())
}

export const getCockpitInitiativesPlanQuality = ({
  initiatives,
  rows,
  partners,
  geoMappingItems,
  businessPriorities,
  keyObjectives,
  offering,
  fYear,
  currentFQuarter,
  fQuarterActualsPercentage,
  selectedMetric,
  selectedCurrency,
  selectedPeriods,
  planningPeriod,
  isLocalCurrency,
  selectedCategory = null,
}: IGetCockpitInitiativesPlanQualityParams) => {
  const programType = offering as ProgramTypes
  const effectivePlanningPeriod = planningPeriod ? { ...planningPeriod, fYear } : null
  const scopedInitiatives = getCockpitInitiativesInScope({
    initiatives,
    fYear,
    selectedOffering: offering,
    selectedCategory,
    selectedPeriods,
    planningPeriod: effectivePlanningPeriod,
  })
  const scopedRows = getFilteredFinSobRows({
    currentFQuarter,
    rows,
    offering: programType,
    periods: selectedPeriods,
    category: selectedCategory,
  })
  const revenueParams = {
    selectedFYear: fYear,
    actualsType: selectedMetric,
    isLocalCurrency,
    currentFQuarter,
    fQuarterActualsPercentage: selectedPeriods.includes('ytd') ? fQuarterActualsPercentage : null,
    category: selectedCategory ?? OVERALL_CATEGORY,
  }
  const plan = getRevenueAndSobPlan({
    ...revenueParams,
    rows: scopedRows.filter(({ isTarget }) => isTarget),
  })
  const baseline = getRevenueAndSobBaseline({
    ...revenueParams,
    rows: scopedRows.filter(isActualsFinSobRow),
  })
  const filteredBusinessPriorities = getBusinessPrioritiesForOffering({
    priorities: businessPriorities,
    partners,
    geoMappingItems,
    initiatives: scopedInitiatives,
    fYear,
    offering: programType,
  })

  return getCockpitInitiativesQuality({
    initiatives: scopedInitiatives,
    businessPriorities: filteredBusinessPriorities,
    keyObjectives,
    selectedMetric,
    selectedCurrency,
    selectedPeriods,
    planningPeriod: effectivePlanningPeriod,
    planSales: plan.sales,
    baselineSales: baseline.sales,
  })
}
